import authService, { TOKEN_TYPES } from "./authService";
import { toBackendTrialId } from "@/utils/trialIdMap";
import type {
  NewVersionPayload,
  RoleAccessPayload,
  StudyDocumentRow,
  UploadDocumentPayload,
} from "@/views/DocumentWorkspace/documentWorkspace.types";

export class DocumentWorkspaceApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "DocumentWorkspaceApiError";
    this.status = status;
  }
}

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/study_documents`;

function buildUploadFormData(
  payload: UploadDocumentPayload | NewVersionPayload,
): FormData {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key === "role_access" && Array.isArray(value)) {
      value.forEach((role: string) => formData.append("role_access", role));
      return;
    }
    formData.append(key, value as string | Blob);
  });
  return formData;
}

async function multipartRequest<T>(
  url: string,
  formData: FormData,
): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  // No Content-Type header — the browser sets the multipart boundary itself.
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      payload && typeof payload.detail === "string"
        ? payload.detail
        : `Request failed (${response.status})`;
    throw new DocumentWorkspaceApiError(detail, response.status);
  }
  return payload as T;
}

async function jsonRequest<T>(
  url: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      payload && typeof payload.detail === "string"
        ? payload.detail
        : `Request failed (${response.status})`;
    throw new DocumentWorkspaceApiError(detail, response.status);
  }
  return payload as T;
}

export const documentWorkspaceService = {
  uploadDocument: (trialId: string, payload: UploadDocumentPayload) =>
    multipartRequest<StudyDocumentRow>(
      `${base(trialId)}/`,
      buildUploadFormData(payload),
    ),

  addVersion: (
    trialId: string,
    documentId: string,
    payload: NewVersionPayload,
  ) =>
    multipartRequest<StudyDocumentRow>(
      `${base(trialId)}/${documentId}/versions/`,
      buildUploadFormData(payload),
    ),

  setRoleAccess: (
    trialId: string,
    documentId: string,
    payload: RoleAccessPayload,
  ) =>
    jsonRequest<StudyDocumentRow>(
      `${base(trialId)}/${documentId}/role_access/`,
      {
        method: "PATCH",
        body: payload,
      },
    ),

  buildVersionContentUrl: (
    trialId: string,
    documentId: string,
    versionId: string,
  ) => `${base(trialId)}/${documentId}/versions/${versionId}/content/`,
};
